// src/pages/admin/Jobs/JobModal.jsx
import React, { useState, useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { jobsService } from '../../../services/jobsService'

export default function JobModal({
    isOpen,
    onClose,
    job,
    mode = 'view',
    onJobCreated,
    onJobUpdated
}) {
    const [activeTab, setActiveTab] = useState('basic')
    const [isSubmitting, setIsSubmitting] = useState(false)
    const [formError, setFormError] = useState('')

    const {
        register,
        handleSubmit,
        reset,
        formState: { errors },
        watch,
        setValue,
        getValues,
        trigger
    } = useForm({
        defaultValues: {
            title: '',
            slug: '',
            location: '',
            type: 'Full-time',
            featured: false,
            isOpen: true,

            // Hero section
            hero: {
                heading: '',
                spans: [{ iconUrl: '', value: '' }],
                isVisible: true
            },

            // About section
            about: {
                heading: '',
                description: '',
                isVisible: true
            },

            // Details section
            details: {
                Heading: '',
                cards: [{
                    title: '',
                    list: ['']
                }],
                isVisible: true
            },

            // Bottom section
            bottomSection: {
                Heading: '',
                Subheading: '',
                buttonText: '',
                buttonLink: '',
                isVisible: true
            },

            // SEO fields
            seoTitle: '',
            seoDescription: '',
            seoKeywords: []
        }
    })

    // Pre-fill form when modal opens or job changes
    useEffect(() => {
        if (isOpen && job) {
            const jobData = {
                ...job,
                // Ensure all sections exist with proper defaults
                hero: job.hero || {
                    heading: '',
                    spans: [{ iconUrl: '', value: '' }],
                    isVisible: true
                },
                about: job.about || {
                    heading: '',
                    description: '',
                    isVisible: true
                },
                details: job.details || {
                    Heading: '',
                    cards: [{
                        title: '',
                        list: ['']
                    }],
                    isVisible: true
                },
                bottomSection: job.bottomSection || {
                    Heading: '',
                    Subheading: '',
                    buttonText: '',
                    buttonLink: '',
                    isVisible: true
                },
                seoTitle: job.seoTitle || '',
                seoDescription: job.seoDescription || '',
                seoKeywords: job.seoKeywords || []
            }
            reset(jobData)
        } else if (mode === 'create' && isOpen) {
            reset({
                title: '',
                slug: '',
                location: '',
                type: 'Full-time',
                featured: false,
                isOpen: true,
                hero: {
                    heading: '',
                    spans: [{ iconUrl: '', value: '' }],
                    isVisible: true
                },
                about: {
                    heading: '',
                    description: '',
                    isVisible: true
                },
                details: {
                    Heading: '',
                    cards: [{
                        title: '',
                        list: ['']
                    }],
                    isVisible: true
                },
                bottomSection: {
                    Heading: '',
                    Subheading: '',
                    buttonText: '',
                    buttonLink: '',
                    isVisible: true
                },
                seoTitle: '',
                seoDescription: '',
                seoKeywords: []
            })
        }
        setActiveTab('basic')
    }, [isOpen, job, mode, reset])

    const onSubmit = async (data) => {
        setIsSubmitting(true)
        setFormError('')

        try {
            // Validate current tab before submitting
            const isValid = await trigger()
            if (!isValid) {
                setFormError('Please fix the validation errors before submitting.')
                setIsSubmitting(false)
                return
            }

            // Clean up data - remove empty items from arrays
            const cleanData = {
                ...data,
                hero: {
                    ...data.hero,
                    spans: data.hero?.spans?.filter(span =>
                        span?.iconUrl?.trim() || span?.value?.trim()
                    ) || []
                },
                details: {
                    ...data.details,
                    cards: data.details?.cards?.map(card => ({
                        ...card,
                        list: card.list?.filter(item => item?.trim() !== '') || []
                    })).filter(card =>
                        card.title.trim() || (card.list && card.list.length > 0)
                    ) || []
                },
                // Ensure all required fields are present
                title: data.title.trim(),
                slug: data.slug.trim(),
                location: data.location.trim(),
                type: data.type,
                featured: data.featured || false,
                isOpen: data.isOpen !== undefined ? data.isOpen : true
            }

            if (mode === 'create') {
                const response = await jobsService.createJob(cleanData)
                if (response.data) {
                    onJobCreated?.()
                }
            } else if (mode === 'edit' && job?._id) {
                const response = await jobsService.updateJob(job._id, cleanData)
                if (response.data) {
                    onJobUpdated?.()
                }
            }
        } catch (error) {
            const errorMessage = error.response?.data?.message || error.message || 'Failed to save job. Please try again.'
            setFormError(errorMessage)
            console.error('Failed to save job:', error)
        } finally {
            setIsSubmitting(false)
        }
    }

    // Array manipulation functions
    const addHeroSpan = () => {
        const spans = getValues('hero.spans') || []
        setValue('hero.spans', [...spans, { iconUrl: '', value: '' }], { shouldValidate: true })
    }

    const removeHeroSpan = (index) => {
        const spans = getValues('hero.spans') || []
        if (spans.length > 1) {
            const newSpans = spans.filter((_, i) => i !== index)
            setValue('hero.spans', newSpans, { shouldValidate: true })
        }
    }

    const addDetailsCard = () => {
        const cards = getValues('details.cards') || []
        setValue('details.cards', [...cards, { title: '', list: [''] }], { shouldValidate: true })
    }

    const removeDetailsCard = (index) => {
        const cards = getValues('details.cards') || []
        if (cards.length > 1) {
            const newCards = cards.filter((_, i) => i !== index)
            setValue('details.cards', newCards, { shouldValidate: true })
        }
    }

    const addCardListItem = (cardIndex) => {
        const cards = getValues('details.cards') || []
        const updatedCards = [...cards]
        if (!updatedCards[cardIndex]) {
            updatedCards[cardIndex] = { title: '', list: [] }
        }
        updatedCards[cardIndex] = {
            ...updatedCards[cardIndex],
            list: [...(updatedCards[cardIndex].list || []), '']
        }
        setValue('details.cards', updatedCards, { shouldValidate: true })
    }

    const removeCardListItem = (cardIndex, listIndex) => {
        const cards = getValues('details.cards') || []
        const updatedCards = [...cards]
        if (updatedCards[cardIndex] && updatedCards[cardIndex].list) {
            updatedCards[cardIndex] = {
                ...updatedCards[cardIndex],
                list: updatedCards[cardIndex].list.filter((_, i) => i !== listIndex)
            }
            setValue('details.cards', updatedCards, { shouldValidate: true })
        }
    }

    const handleModalClose = () => {
        reset()
        setActiveTab('basic')
        setFormError('')
        onClose(false)
    }

    const tabs = [
        { id: 'basic', name: 'Basic Info' },
        { id: 'hero', name: 'Hero Section' },
        { id: 'about', name: 'About Role' },
        { id: 'details', name: 'Job Details' },
        { id: 'bottomSection', name: 'How to Apply' },
        { id: 'seo', name: 'SEO' }
    ]

    if (!isOpen) return null

    return (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
            <div className="relative top-20 mx-auto p-5 border w-full max-w-6xl shadow-lg rounded-lg bg-white max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex justify-between items-center pb-4 border-b">
                    <h3 className="text-xl font-bold text-gray-900">
                        {mode === 'create' ? 'Create New Job' : mode === 'edit' ? 'Edit Job' : 'Job Details'} {job?.title ? `- ${job.title}` : ''}
                    </h3>
                    <button
                        onClick={handleModalClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors duration-200"
                    >
                        <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                        </svg>
                    </button>
                </div>

                {/* Form Error */}
                {formError && (
                    <div className="mt-4 bg-red-50 border border-red-200 rounded-lg p-4">
                        <div className="flex items-center">
                            <svg className="w-5 h-5 text-red-400 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <span className="text-red-700 text-sm">{formError}</span>
                        </div>
                    </div>
                )}

                {/* Tabs */}
                <div className="border-b border-gray-200 mt-4">
                    <nav className="-mb-px flex space-x-8 overflow-x-auto">
                        {tabs.map((tab) => (
                            <button
                                key={tab.id}
                                onClick={() => setActiveTab(tab.id)}
                                className={`whitespace-nowrap py-4 px-1 border-b-2 font-medium text-sm ${activeTab === tab.id
                                    ? 'border-primary-500 text-primary-600'
                                    : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                                    }`}
                            >
                                {tab.name}
                            </button>
                        ))}
                    </nav>
                </div>

                {/* Form */}
                <form onSubmit={handleSubmit(onSubmit)} className="mt-6">
                    {/* Basic Info Tab */}
                    {activeTab === 'basic' && (
                        <div className="space-y-6">
                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Job Title *
                                    </label>
                                    <input
                                        type="text"
                                        {...register('title', { required: 'Job title is required' })}
                                        disabled={mode === 'view'}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.title ? 'border-red-500' : 'border-gray-300'
                                            } ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder="e.g., Senior Frontend Developer"
                                    />
                                    {errors.title && (
                                        <p className="text-red-500 text-sm mt-1">{errors.title.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Slug *
                                    </label>
                                    <input
                                        type="text"
                                        {...register('slug', {
                                            required: 'Slug is required',
                                            pattern: {
                                                value: /^[a-z0-9-]+$/,
                                                message: 'Slug can only contain lowercase letters, numbers, and hyphens'
                                            }
                                        })}
                                        disabled={mode === 'view'}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.slug ? 'border-red-500' : 'border-gray-300'
                                            } ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder="e.g., senior-frontend-developer"
                                    />
                                    {errors.slug && (
                                        <p className="text-red-500 text-sm mt-1">{errors.slug.message}</p>
                                    )}
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Location *
                                    </label>
                                    <input
                                        type="text"
                                        {...register('location', { required: 'Location is required' })}
                                        disabled={mode === 'view'}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.location ? 'border-red-500' : 'border-gray-300'
                                            } ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                        placeholder="e.g., Remote, New York, On-Site"
                                    />
                                    {errors.location && (
                                        <p className="text-red-500 text-sm mt-1">{errors.location.message}</p>
                                    )}
                                </div>

                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Job Type *
                                    </label>
                                    <select
                                        {...register('type', { required: 'Job type is required' })}
                                        disabled={mode === 'view'}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.type ? 'border-red-500' : 'border-gray-300'
                                            } ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    >
                                        <option value="Full-time">Full-time</option>
                                        <option value="Part-time">Part-time</option>
                                        <option value="Contract">Contract</option>
                                        <option value="Remote">Remote</option>
                                        <option value="Hybrid">Hybrid</option>
                                    </select>
                                    {errors.type && (
                                        <p className="text-red-500 text-sm mt-1">{errors.type.message}</p>
                                    )}
                                </div>
                            </div>

                            <div className="flex items-center space-x-6">
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        {...register('featured')}
                                        disabled={mode === 'view'}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm text-gray-700">
                                        Featured Job
                                    </label>
                                </div>
                                <div className="flex items-center">
                                    <input
                                        type="checkbox"
                                        {...register('isOpen')}
                                        disabled={mode === 'view'}
                                        className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                    />
                                    <label className="ml-2 block text-sm text-gray-700">
                                        Job Open for Applications
                                    </label>
                                </div>
                            </div>
                        </div>
                    )}

                    {/* Hero Section Tab */}
                    {activeTab === 'hero' && (
                        <div className="space-y-6">
                            <div className="flex items-center mb-4 p-3 bg-gray-50 rounded-lg">
                                <input
                                    type="checkbox"
                                    {...register('hero.isVisible')}
                                    disabled={mode === 'view'}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm font-medium text-gray-700">
                                    Show Hero Section
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Hero Heading
                                </label>
                                <input
                                    type="text"
                                    {...register('hero.heading')}
                                    disabled={mode === 'view'}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                        }`}
                                    placeholder="e.g., IT Project Coordinator"
                                />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Hero Spans (Icon + Text Pairs)
                                    </label>
                                    {mode !== 'view' && (
                                        <button
                                            type="button"
                                            onClick={addHeroSpan}
                                            className="text-sm bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700"
                                        >
                                            Add Span
                                        </button>
                                    )}
                                </div>

                                {getValues('hero.spans')?.map((_, index) => (
                                    <div key={index} className="border border-gray-200 rounded-lg p-4 mb-4">
                                        <div className="flex justify-between items-center mb-3">
                                            <h4 className="text-sm font-medium text-gray-700">Span {index + 1}</h4>
                                            {mode !== 'view' && getValues('hero.spans')?.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeHeroSpan(index)}
                                                    className="text-red-600 hover:text-red-800 text-sm"
                                                >
                                                    Remove
                                                </button>
                                            )}
                                        </div>
                                        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Icon URL</label>
                                                <input
                                                    type="url"
                                                    {...register(`hero.spans.${index}.iconUrl`)}
                                                    disabled={mode === 'view'}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    placeholder="https://example.com/icon.svg"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">SVG or PNG icon URL</p>
                                            </div>
                                            <div>
                                                <label className="block text-xs font-medium text-gray-600 mb-1">Text Value</label>
                                                <input
                                                    type="text"
                                                    {...register(`hero.spans.${index}.value`)}
                                                    disabled={mode === 'view'}
                                                    className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                    placeholder="e.g., Management, On-Site, Full-time"
                                                />
                                                <p className="text-xs text-gray-500 mt-1">Short descriptive text</p>
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* About Role Tab */}
                    {activeTab === 'about' && (
                        <div className="space-y-6">
                            <div className="flex items-center mb-4 p-3 bg-gray-50 rounded-lg">
                                <input
                                    type="checkbox"
                                    {...register('about.isVisible')}
                                    disabled={mode === 'view'}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm font-medium text-gray-700">
                                    Show About Section
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    About Heading
                                </label>
                                <input
                                    type="text"
                                    {...register('about.heading')}
                                    disabled={mode === 'view'}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                        }`}
                                    placeholder="e.g., About the Role"
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Description *
                                </label>
                                <textarea
                                    rows={8}
                                    {...register('about.description', { required: 'Description is required' })}
                                    disabled={mode === 'view'}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.about?.description ? 'border-red-500' : 'border-gray-300'
                                        } ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''}`}
                                    placeholder="Detailed description about this job role, responsibilities, and what the candidate will be doing..."
                                />
                                {errors.about?.description && (
                                    <p className="text-red-500 text-sm mt-1">{errors.about.description.message}</p>
                                )}
                                <p className="text-xs text-gray-500 mt-1">Provide a comprehensive overview of the role</p>
                            </div>
                        </div>
                    )}

                    {/* Job Details Tab */}
                    {activeTab === 'details' && (
                        <div className="space-y-6">
                            <div className="flex items-center mb-4 p-3 bg-gray-50 rounded-lg">
                                <input
                                    type="checkbox"
                                    {...register('details.isVisible')}
                                    disabled={mode === 'view'}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm font-medium text-gray-700">
                                    Show Details Section
                                </label>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    Section Heading
                                </label>
                                <input
                                    type="text"
                                    {...register('details.Heading')}
                                    disabled={mode === 'view'}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                        }`}
                                    placeholder="e.g., Role Details"
                                />
                            </div>

                            <div>
                                <div className="flex justify-between items-center mb-4">
                                    <label className="block text-sm font-medium text-gray-700">
                                        Detail Cards (e.g., Responsibilities, Requirements, Qualifications)
                                    </label>
                                    {mode !== 'view' && (
                                        <button
                                            type="button"
                                            onClick={addDetailsCard}
                                            className="text-sm bg-primary-600 text-white px-3 py-1 rounded hover:bg-primary-700"
                                        >
                                            Add Card
                                        </button>
                                    )}
                                </div>

                                {getValues('details.cards')?.map((card, cardIndex) => (
                                    <div key={cardIndex} className="border border-gray-200 rounded-lg p-4 mb-6">
                                        <div className="flex justify-between items-center mb-4">
                                            <h4 className="text-sm font-medium text-gray-700">Card {cardIndex + 1}</h4>
                                            {mode !== 'view' && getValues('details.cards')?.length > 1 && (
                                                <button
                                                    type="button"
                                                    onClick={() => removeDetailsCard(cardIndex)}
                                                    className="text-red-600 hover:text-red-800 text-sm"
                                                >
                                                    Remove Card
                                                </button>
                                            )}
                                        </div>

                                        <div className="mb-4">
                                            <label className="block text-xs font-medium text-gray-600 mb-1">Card Title *</label>
                                            <input
                                                type="text"
                                                {...register(`details.cards.${cardIndex}.title`, { required: 'Card title is required' })}
                                                disabled={mode === 'view'}
                                                className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 ${errors.details?.cards?.[cardIndex]?.title ? 'border-red-500' : 'border-gray-300'
                                                    }`}
                                                placeholder="e.g., Responsibilities, Requirements, Preferred Qualifications"
                                            />
                                        </div>

                                        <div>
                                            <div className="flex justify-between items-center mb-2">
                                                <label className="block text-xs font-medium text-gray-600">
                                                    List Items
                                                </label>
                                                {mode !== 'view' && (
                                                    <button
                                                        type="button"
                                                        onClick={() => addCardListItem(cardIndex)}
                                                        className="text-xs bg-gray-600 text-white px-2 py-1 rounded hover:bg-gray-700"
                                                    >
                                                        Add Item
                                                    </button>
                                                )}
                                            </div>
                                            {card.list?.map((item, listIndex) => (
                                                <div key={listIndex} className="flex gap-2 mb-2">
                                                    <input
                                                        type="text"
                                                        {...register(`details.cards.${cardIndex}.list.${listIndex}`, {
                                                            required: 'List item cannot be empty',
                                                            validate: value => value.trim() !== '' || 'List item cannot be empty'
                                                        })}
                                                        disabled={mode === 'view'}
                                                        className="flex-1 px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500"
                                                        placeholder="e.g., Develop and maintain web applications"
                                                    />
                                                    {mode !== 'view' && card.list.length > 1 && (
                                                        <button
                                                            type="button"
                                                            onClick={() => removeCardListItem(cardIndex, listIndex)}
                                                            className="px-3 py-2 text-red-600 hover:text-red-800"
                                                        >
                                                            ×
                                                        </button>
                                                    )}
                                                </div>
                                            ))}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    )}

                    {/* How to Apply Tab */}
                    {activeTab === 'bottomSection' && (
                        <div className="space-y-6">
                            <div className="flex items-center mb-4 p-3 bg-gray-50 rounded-lg">
                                <input
                                    type="checkbox"
                                    {...register('bottomSection.isVisible')}
                                    disabled={mode === 'view'}
                                    className="h-4 w-4 text-primary-600 focus:ring-primary-500 border-gray-300 rounded"
                                />
                                <label className="ml-2 block text-sm font-medium text-gray-700">
                                    Show "How to Apply" Section
                                </label>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Heading
                                    </label>
                                    <input
                                        type="text"
                                        {...register('bottomSection.Heading')}
                                        disabled={mode === 'view'}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                            }`}
                                        placeholder="e.g., How to Apply"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Subheading
                                    </label>
                                    <input
                                        type="text"
                                        {...register('bottomSection.Subheading')}
                                        disabled={mode === 'view'}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                            }`}
                                        placeholder="e.g., Submit your application today"
                                    />
                                </div>
                            </div>

                            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Button Text
                                    </label>
                                    <input
                                        type="text"
                                        {...register('bottomSection.buttonText')}
                                        disabled={mode === 'view'}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                            }`}
                                        placeholder="e.g., Apply Now"
                                    />
                                </div>
                                <div>
                                    <label className="block text-sm font-medium text-gray-700 mb-2">
                                        Button Link
                                    </label>
                                    <input
                                        type="url"
                                        {...register('bottomSection.buttonLink')}
                                        disabled={mode === 'view'}
                                        className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                            }`}
                                        placeholder="https://example.com/apply or mailto:careers@company.com"
                                    />
                                </div>
                            </div>
                        </div>
                    )}

                    {/* SEO Tab */}
                    {activeTab === 'seo' && (
                        <div className="space-y-6">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    SEO Title
                                </label>
                                <input
                                    type="text"
                                    {...register('seoTitle')}
                                    disabled={mode === 'view'}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                        }`}
                                    placeholder="e.g., IT Project Coordinator Job | Your Company"
                                />
                                <p className="text-xs text-gray-500 mt-1">Appears in search engine results (50-60 characters recommended)</p>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-2">
                                    SEO Description
                                </label>
                                <textarea
                                    rows={4}
                                    {...register('seoDescription')}
                                    disabled={mode === 'view'}
                                    className={`w-full px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 border-gray-300 ${mode === 'view' ? 'bg-gray-100 cursor-not-allowed' : ''
                                        }`}
                                    placeholder="e.g., Apply for IT Project Coordinator position at Your Company. Manage project timelines, documentation, and stakeholder communication. Join our growing team."
                                />
                                <p className="text-xs text-gray-500 mt-1">Brief summary for search engines (150-160 characters recommended)</p>
                            </div>
                        </div>
                    )}

                    {/* Action Buttons */}
                    {mode !== 'view' && (
                        <div className="flex justify-end space-x-3 pt-6 mt-6 border-t">
                            <button
                                type="button"
                                onClick={handleModalClose}
                                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-200"
                                disabled={isSubmitting}
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isSubmitting}
                                className="px-6 py-2 text-sm font-medium text-white bg-primary-600 border border-transparent rounded-lg hover:bg-primary-700 focus:outline-none focus:ring-2 focus:ring-primary-500 disabled:opacity-50 disabled:cursor-not-allowed transition-colors duration-200"
                            >
                                {isSubmitting ? 'Saving...' : mode === 'create' ? 'Create Job' : 'Update Job'}
                            </button>
                        </div>
                    )}

                    {mode === 'view' && (
                        <div className="flex justify-end space-x-3 pt-6 mt-6 border-t">
                            <button
                                type="button"
                                onClick={handleModalClose}
                                className="px-6 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-primary-500 transition-colors duration-200"
                            >
                                Close
                            </button>
                        </div>
                    )}
                </form>
            </div>
        </div>
    )
}